"use client";

import { useEffect, useRef, useState } from "react";
import { useJourneyDailyLimit } from "@/hooks/useJourneyDailyLimit";
import {
  LuGraduationCap,
  LuPlane,
  LuBriefcase,
  LuRoute,
  LuInfo,
  LuListChecks,
  LuLightbulb,
  LuCoins,
  LuTrendingUp,
  LuDollarSign,
  LuTarget,
  LuCircleAlert,
  LuCircleCheckBig,
} from "react-icons/lu";
import countryDataJson from "@/json/countryData.json";
import Link from "next/link";
import Image from "next/image";

type TabKey = "study" | "travel" | "work";
type StudyArticle = {
  introduction: string;
  benefits: string;
  costs: string;
  opportunities: string;
  risks: string;
  overallAssessment: string;
};
type CountryInfo = {
  general: string[];
  process: string[];
  tips: string[];
  costs: string[];
  article?: StudyArticle;
};

const markerCountries = countryDataJson.markerCountries as unknown as Record<
  string,
  { label: string; coords: [number, number]; flagCode: string }
>;
const DATA = countryDataJson.countryData as unknown as Record<
  string,
  Record<TabKey, CountryInfo>
>;
const IMAGES = countryDataJson.countryImages as unknown as Record<
  string,
  Record<TabKey, string>
>;

const COUNTRY_TO_SLUG: Record<string, string> = {
  "United States of America": "usa",
  "United Kingdom": "uk",
  Canada: "canada",
  Australia: "australia",
  Japan: "japan",
  China: "china",
  Germany: "germany",
  France: "france",
  Sweden: "sweden",
  Finland: "finland",
  "New Zealand": "new-zealand",
  Norway: "norway",
  Malaysia: "malaysia",
  Singapore: "singapore",
  Thailand: "thailand",
  Turkey: "turkey",
  India: "india",
  Pakistan: "pakistan",
};
const SLUG_TO_COUNTRY = Object.fromEntries(
  Object.entries(COUNTRY_TO_SLUG).map(([name, slug]) => [slug, name]),
);

const TAB_META: Record<TabKey, { label: string; accent: string }> = {
  study: { label: "Study in", accent: "indigo" },
  travel: { label: "Travel to", accent: "sky" },
  work: { label: "Resettle in", accent: "emerald" },
};

// ─── Globe HTML (mirrors GlobeForApp.tsx, adapted for web iframe) ─────────────

function buildGlobeHTML(markersJson: string, isDark: boolean): string {
  const bg = isDark ? "#020618" : "#f0f4f8";
  const globeImage = isDark
    ? "https://unpkg.com/three-globe/example/img/earth-night.jpg"
    : "https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg";
  const atmosphereColor = isDark ? "#7c3aed" : "#93c5fd";
  const pillBg = isDark ? "rgba(13,21,38,0.88)" : "rgba(255,255,255,0.92)";
  const pillColor = isDark ? "#fff" : "#0f172a";
  const pillBorder = isDark ? "#7c3aed" : "#818cf8";

  return `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no">
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  html,body { width:100%; height:100%; background:${bg}; overflow:hidden; }
  #globe { width:100%; height:100%; }
  .pill {
    display:flex; align-items:center; gap:5px;
    background:${pillBg};
    border:1px solid ${pillBorder};
    border-radius:999px;
    padding:3px 9px 3px 5px;
    cursor:pointer; white-space:nowrap;
    font-family:-apple-system,sans-serif;
    font-size:11px; font-weight:700; color:${pillColor};
    pointer-events:auto;
    transition:background 0.2s,border-color 0.2s;
  }
  .pill.selected { background:#7c3aed; border-color:#a78bfa; color:#fff; }
  .pill:active { opacity:0.8; }
  .pill img { width:18px; height:18px; border-radius:50%; object-fit:cover; flex-shrink:0; }
</style>
</head>
<body>
<div id="globe"></div>
<script src="https://unpkg.com/globe.gl@2/dist/globe.gl.min.js"></script>
<script>
var MARKERS = ${markersJson};
var selected = null;

var markerArr = Object.entries(MARKERS).map(function(e) {
  return { name: e[0], label: e[1].label, lng: e[1].coords[0], lat: e[1].coords[1], flagCode: e[1].flagCode };
});

var globe = Globe()
  .globeImageUrl('${globeImage}')
  .backgroundColor('${bg}')
  .showAtmosphere(true)
  .atmosphereColor('${atmosphereColor}')
  .atmosphereAltitude(0.16)
  .htmlElementsData(markerArr)
  .htmlLat(function(d) { return d.lat; })
  .htmlLng(function(d) { return d.lng; })
  .htmlAltitude(0.01)
  .htmlElement(function(d) {
    var el = document.createElement('div');
    el.className = 'pill' + (d.name === selected ? ' selected' : '');
    el.dataset.country = d.name;
    el.innerHTML = '<img src="https://flagcdn.com/w40/' + d.flagCode + '.png"/><span>' + d.label + '</span>';
    el.addEventListener('click', function() {
      window.parent.postMessage(d.name, '*');
    });
    return el;
  })
  (document.getElementById('globe'));

globe.controls().autoRotate = true;
globe.controls().autoRotateSpeed = 0.35;
globe.controls().enableZoom = true;
globe.pointOfView({ lat: 25, lng: 15, altitude: 2.4 });

var resumeTimer = null;
globe.controls().addEventListener('start', function() {
  globe.controls().autoRotate = false;
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = setTimeout(function() { globe.controls().autoRotate = true; }, 4000);
});

window.selectCountry = function(name) {
  selected = name;
  globe.htmlElementsData(markerArr.slice());
  if (!name) return;
  var m = markerArr.find(function(x) { return x.name === name; });
  if (m) globe.pointOfView({ lat: m.lat, lng: m.lng, altitude: 2.0 }, 800);
};

window.addEventListener('message', function(e) {
  if (!e.data || typeof e.data !== 'object') return;
  if (e.data.type === 'selectCountry') {
    window.selectCountry(e.data.name);
  } else if (e.data.type === 'setTheme') {
    applyTheme(e.data.isDark);
  }
});

function applyTheme(isDark) {
  var bg = isDark ? '#020618' : '#f0f4f8';
  var globeImage = isDark
    ? 'https://unpkg.com/three-globe/example/img/earth-night.jpg'
    : 'https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg';
  var atmosphereColor = isDark ? '#7c3aed' : '#93c5fd';
  var pillBg = isDark ? 'rgba(13,21,38,0.88)' : 'rgba(255,255,255,0.92)';
  var pillColor = isDark ? '#fff' : '#0f172a';
  var pillBorder = isDark ? '#7c3aed' : '#818cf8';
  document.body.style.background = bg;
  var s = document.getElementById('_th');
  if (!s) { s = document.createElement('style'); s.id = '_th'; document.head.appendChild(s); }
  s.textContent = '.pill:not(.selected){background:' + pillBg + '!important;border-color:' + pillBorder + '!important;color:' + pillColor + '!important;}';
  if (typeof globe !== 'undefined') {
    globe.globeImageUrl(globeImage);
    globe.backgroundColor(bg);
    globe.atmosphereColor(atmosphereColor);
  }
}
</script>
</body>
</html>`;
}

// ─────────────────────────────────────────────────────────────────────────────

function parseParas(text: string) {
  return text.replace(/\\n/g, "\n").split(/\n\n+/).filter(Boolean);
}

function ArticleSection({
  tab,
  country,
  article,
}: {
  tab: TabKey;
  country: string;
  article: StudyArticle;
}) {
  const meta = TAB_META[tab];
  const accentBorder = {
    indigo: "border-indigo-500",
    sky: "border-sky-500",
    emerald: "border-emerald-500",
  }[meta.accent];
  const accentText = {
    indigo: "text-indigo-600 dark:text-indigo-400",
    sky: "text-sky-600 dark:text-sky-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
  }[meta.accent];
  const accentBg = {
    indigo:
      "bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700/40",
    sky: "bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-700/40",
    emerald:
      "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700/40",
  }[meta.accent];

  return (
    <section className="max-w-325 mx-auto px-5 pb-15">
      <h2 className="text-2xl lg:text-3xl font-bold mb-8 flex items-center gap-3">
        {tab === "study" ? (
          <LuGraduationCap size={28} />
        ) : tab === "travel" ? (
          <LuPlane size={28} />
        ) : (
          <LuBriefcase size={28} />
        )}
        {meta.label} {country}
      </h2>

      <p
        className={`text-base lg:text-[17px] leading-relaxed text-gray-700 dark:text-gray-300 mb-10 pl-5 border-l-4 ${accentBorder} font-medium italic`}
      >
        {article.introduction}
      </p>

      <div className="mb-8">
        <h3
          className={`flex items-center gap-2 text-lg font-bold mb-4 ${accentText}`}
        >
          <LuTrendingUp size={20} />
          Benefits
        </h3>
        <div className="space-y-4">
          {parseParas(article.benefits).map((para, i) => {
            const titleMatch = para.match(/^([A-Z][^:\n]{0,60}):\s/);
            if (titleMatch) {
              return (
                <p
                  key={i}
                  className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300"
                >
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {titleMatch[1]}:{" "}
                  </span>
                  {para.slice(titleMatch[0].length)}
                </p>
              );
            }
            return (
              <p
                key={i}
                className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300"
              >
                {para}
              </p>
            );
          })}
        </div>
      </div>

      <hr className="border-gray-200 dark:border-white/10 mb-8" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold mb-3 text-orange-600 dark:text-orange-400">
            <LuDollarSign size={18} />
            Costs
          </h3>
          <div className="space-y-3">
            {parseParas(article.costs).map((para, i) => (
              <p
                key={i}
                className="text-[14px] leading-relaxed text-gray-600 dark:text-gray-400"
              >
                {para}
              </p>
            ))}
          </div>
        </div>
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold mb-3 text-blue-600 dark:text-blue-400">
            <LuTarget size={18} />
            Opportunities
          </h3>
          <div className="space-y-3">
            {parseParas(article.opportunities).map((para, i) => (
              <p
                key={i}
                className="text-[14px] leading-relaxed text-gray-600 dark:text-gray-400"
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl p-5 bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-700/30 mb-8">
        <h3 className="flex items-center gap-2 text-base font-bold mb-3 text-red-600 dark:text-red-400">
          <LuCircleAlert size={18} />
          Risks & Considerations
        </h3>
        <div className="space-y-3">
          {parseParas(article.risks).map((para, i) => (
            <p
              key={i}
              className="text-[14px] leading-relaxed text-gray-700 dark:text-gray-300"
            >
              {para}
            </p>
          ))}
        </div>
      </div>

      <div className={`rounded-2xl p-6 border ${accentBg}`}>
        <h3
          className={`flex items-center gap-2 text-base font-bold mb-3 ${accentText}`}
        >
          <LuCircleCheckBig size={18} />
          Overall Assessment
        </h3>
        <p className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 font-medium">
          {article.overallAssessment}
        </p>
      </div>
    </section>
  );
}

export default function ExploreContent() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isDarkRef = useRef(false);
  // Tracks the last country sent to the globe so we can re-send after iframe reloads
  const pendingGlobeCountryRef = useRef<string | null>(null);

  const generalListRef = useRef<HTMLUListElement | null>(null);
  const processListRef = useRef<HTMLUListElement | null>(null);
  const tipsListRef = useRef<HTMLUListElement | null>(null);
  const costsListRef = useRef<HTMLUListElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const leftFlagRef = useRef<HTMLImageElement | null>(null);
  const selectedNameRef = useRef<HTMLHeadingElement | null>(null);
  const initialCountryRef = useRef<string | null>(null);
  const skipNextScrollRef = useRef(false);
  const [activeTab, setactiveTab] = useState("study");
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [globeHtml, setGlobeHtml] = useState("");
  const dailyLimit = useJourneyDailyLimit();

  // Build globe HTML client-side only (needs dark mode from DOM)
  useEffect(() => {
    const dark = document.documentElement.classList.contains("dark");
    isDarkRef.current = dark;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only value; reads DOM dark-mode class, must run after mount
    setGlobeHtml(buildGlobeHTML(JSON.stringify(markerCountries), dark));
  }, []);

  // Read slug from URL on first mount
  useEffect(() => {
    const slug = window.location.pathname.split("/").pop() ?? "";
    const name = SLUG_TO_COUNTRY[slug];
    if (name) initialCountryRef.current = name;
  }, []);

  useEffect(() => {
    if (selectedCountry && summaryRef.current) {
      if (skipNextScrollRef.current) {
        skipNextScrollRef.current = false;
        return;
      }
      const top =
        summaryRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [selectedCountry]);

  // Watch <html> class changes to keep globe theme in sync
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const dark = document.documentElement.classList.contains("dark");
      if (dark !== isDarkRef.current) {
        isDarkRef.current = dark;
        iframeRef.current?.contentWindow?.postMessage(
          { type: "setTheme", isDark: dark },
          "*",
        );
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let activeCountryName: string | null = null;
    let activeTab: TabKey = "study";

    function renderLists(g: string[], p: string[], t: string[], c: string[]) {
      if (generalListRef.current)
        generalListRef.current.innerHTML = g
          .map(
            (li, idx, arr) =>
              `<li class="text-[15px] py-2.5 border-b border-gray-200 dark:border-white/8 ${
                idx === arr.length - 1 ? "last:border-b-0" : ""
              }">${li}</li>`,
          )
          .join("");
      if (processListRef.current)
        processListRef.current.innerHTML = p
          .map(
            (li, idx, arr) =>
              `<li class="text-[15px] py-2.5 border-b border-gray-200 dark:border-white/8 ${
                idx === arr.length - 1 ? "last:border-b-0" : ""
              }">${li}</li>`,
          )
          .join("");
      if (tipsListRef.current)
        tipsListRef.current.innerHTML = t
          .map(
            (li, idx, arr) =>
              `<li class="text-[15px] py-2.5 border-b border-gray-200 dark:border-white/8 ${
                idx === arr.length - 1 ? "last:border-b-0" : ""
              }">${li}</li>`,
          )
          .join("");
      if (costsListRef.current)
        costsListRef.current.innerHTML = c
          .map(
            (li, idx, arr) =>
              `<li class="text-[15px] py-2.5 border-b border-gray-200 dark:border-white/8 ${
                idx === arr.length - 1 ? "last:border-b-0" : ""
              }">${li}</li>`,
          )
          .join("");
    }

    function applyCountryUI(countryName: string) {
      if (selectedNameRef.current)
        selectedNameRef.current.textContent = countryName;

      const countryData = markerCountries[countryName];
      if (countryData && countryData.flagCode) {
        const flagUrl = `https://flagcdn.com/w40/${countryData.flagCode}.png`;
        if (leftFlagRef.current) {
          leftFlagRef.current.src = flagUrl;
          leftFlagRef.current.style.display = "block";
        }
      }

      const bundle = DATA[countryName]?.[activeTab as TabKey];
      if (!bundle) {
        renderLists(["No data for this tab."], ["—"], ["—"], ["—"]);
        return;
      }
      renderLists(bundle.general, bundle.process, bundle.tips, bundle.costs);
    }

    function selectCountryByName(
      countryName: string,
      pushToHistory = true,
    ) {
      activeCountryName = countryName;
      setSelectedCountry(countryName);
      applyCountryUI(countryName);
      pendingGlobeCountryRef.current = countryName;
      iframeRef.current?.contentWindow?.postMessage(
        { type: "selectCountry", name: countryName },
        "*",
      );
      if (pushToHistory) {
        const slug = COUNTRY_TO_SLUG[countryName];
        if (slug)
          window.history.pushState(
            { country: slug },
            "",
            `/country-explorer/${slug}`,
          );
      }
    }

    // Receive country clicks from the globe inside the iframe
    function handleMessage(e: MessageEvent) {
      if (typeof e.data === "string" && DATA[e.data]) {
        selectCountryByName(e.data);
      }
    }
    window.addEventListener("message", handleMessage);

    // Pre-select country from URL on initial load (no scroll)
    if (initialCountryRef.current) {
      const name = initialCountryRef.current;
      initialCountryRef.current = null;
      skipNextScrollRef.current = true;
      selectCountryByName(name, false);
    }

    // Back/forward navigation
    function onPopState() {
      const slug = window.location.pathname.split("/").pop() ?? "";
      const name = SLUG_TO_COUNTRY[slug];
      if (name) {
        selectCountryByName(name, false);
      } else {
        activeCountryName = null;
        setSelectedCountry(null);
        pendingGlobeCountryRef.current = null;
        if (leftFlagRef.current) leftFlagRef.current.style.display = "none";
        if (selectedNameRef.current)
          selectedNameRef.current.textContent =
            "Select a country to view details";
        iframeRef.current?.contentWindow?.postMessage(
          { type: "selectCountry", name: null },
          "*",
        );
      }
    }
    window.addEventListener("popstate", onPopState);

    // Tabs
    const tabsEl = tabsRef.current;
    function onTabClick(e: Event) {
      const btn = (e.target as HTMLElement).closest(
        ".tab-button",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      btn
        .parentElement!.querySelectorAll(".tab-button")
        .forEach((t) => t.classList.remove("active"));
      btn.classList.add("active");
      const which = (btn.dataset.tab as TabKey) || "study";
      activeTab = which;
      setactiveTab(activeTab);
      if (activeCountryName) applyCountryUI(activeCountryName);
    }
    tabsEl?.addEventListener("click", onTabClick);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("popstate", onPopState);
      tabsEl?.removeEventListener("click", onTabClick);
    };
  }, []);

  // When iframe finishes loading, apply current theme and re-select pending country
  function handleIframeLoad() {
    const dark = document.documentElement.classList.contains("dark");
    iframeRef.current?.contentWindow?.postMessage(
      { type: "setTheme", isDark: dark },
      "*",
    );
    if (pendingGlobeCountryRef.current) {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "selectCountry", name: pendingGlobeCountryRef.current },
        "*",
      );
    }
  }

  return (
    <div className="page">
      {/* Globe */}
      <section className="globe-wrapper">
        {globeHtml && (
          <iframe
            ref={iframeRef}
            srcDoc={globeHtml}
            className="globe-iframe"
            onLoad={handleIframeLoad}
            title="Interactive Globe"
          />
        )}
      </section>

      {/* Selected country summary */}
      <div ref={summaryRef} className="max-w-325 mx-auto px-5 mt-4 lg:mt-8">
        <div className="flex items-center justify-center gap-3 text-xl lg:text-3xl font-bold">
          {/* eslint-disable-next-line @next/next/no-img-element -- src set imperatively via ref to remote flagcdn.com, not in next.config images.remotePatterns */}
          <img
            ref={leftFlagRef}
            alt="flag"
            className="size-4 lg:size-8 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
            style={{ display: "none" }}
          />
          <h2 ref={selectedNameRef}>Select a country to view details</h2>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{ top: 'calc(3.5rem + var(--ann-h, 0px))' }}
        className="sticky z-30 bg-white/90 dark:bg-[#0a0a0a]/90 backdrop-blur-md border-b border-gray-200 dark:border-white/10"
        ref={tabsRef}
      >
        <div className="max-w-325 mx-auto px-5 py-3 flex gap-1 lg:gap-3">
          <button
            className="tab-button flex items-center gap-2 bg-white dark:bg-[#1a1a1a] text-black dark:text-white border-2 border-black dark:border-white px-2 lg:px-5.5 py-1 lg:py-2.5 rounded-full font-bold text-xs lg:text-sm cursor-pointer transition-all duration-200 hover:bg-[#f3f3f3] dark:hover:bg-[#2a2a2a] [&.active]:bg-black [&.active]:dark:bg-white [&.active]:text-white [&.active]:dark:text-black [&.active]:border-white [&.active]:dark:border-black active"
            data-tab="study"
          >
            <LuGraduationCap size={18} />
            Study
          </button>
          <button
            className="tab-button flex items-center gap-2 bg-white dark:bg-[#1a1a1a] text-black dark:text-white border-2 border-black dark:border-white px-2 lg:px-5.5 py-1 lg:py-2.5 rounded-full font-bold text-xs lg:text-sm cursor-pointer transition-all duration-200 hover:bg-[#f3f3f3] dark:hover:bg-[#2a2a2a] [&.active]:bg-black [&.active]:dark:bg-white [&.active]:text-white [&.active]:dark:text-black [&.active]:border-white [&.active]:dark:border-black"
            data-tab="travel"
          >
            <LuPlane size={18} />
            Travel
          </button>
          <button
            className="tab-button flex items-center gap-2 bg-white dark:bg-[#1a1a1a] text-black dark:text-white border-2 border-black dark:border-white px-2 lg:px-5.5 py-1 lg:py-2.5 rounded-full font-bold text-xs lg:text-sm cursor-pointer transition-all duration-200 hover:bg-[#f3f3f3] dark:hover:bg-[#2a2a2a] [&.active]:bg-black [&.active]:dark:bg-white [&.active]:text-white [&.active]:dark:text-black [&.active]:border-white [&.active]:dark:border-black"
            data-tab="work"
          >
            <LuBriefcase size={18} />
            Work / Resettle
          </button>
          <span className="flex-1 max-lg:hidden"></span>
          {activeTab === "study" &&
            (dailyLimit.limitReached ? (
              <div className="max-sm:hidden flex items-center gap-2">
                <span className="flex items-center gap-2 bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 lg:px-5.5 py-1 lg:py-2.5 rounded-full font-bold text-xs cursor-not-allowed opacity-60 select-none">
                  <LuRoute size={18} />
                  Create journey
                </span>
                <span className="text-xs text-red-500 dark:text-red-400 whitespace-nowrap">
                  Daily limit reached.
                  <br />
                  Resets at 12:00 AM BDT
                </span>
              </div>
            ) : (
              <Link
                className="max-sm:hidden flex items-center gap-2 bg-indigo-500 text-white px-2 lg:px-5.5 py-1 lg:py-2.5 rounded-full font-bold text-xs cursor-pointer transition-all duration-200"
                href="/questionnaire"
              >
                <LuRoute size={18} />
                Create journey
              </Link>
            ))}
        </div>
      </div>

      {/* Hero image — shown when a country is selected */}
      {selectedCountry && IMAGES[selectedCountry]?.[activeTab as TabKey] && (
        <div className="max-w-325 mx-auto px-5 mb-5">
          <div className="relative w-full h-56 lg:h-90 rounded-[22px] overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.22)]">
            <Image
              src={IMAGES[selectedCountry][activeTab as TabKey]}
              alt={`${selectedCountry} ${activeTab}`}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 lg:p-8">
              <p className="text-white/70 text-sm font-medium mb-1">
                {activeTab === "study"
                  ? "Study in"
                  : activeTab === "travel"
                    ? "Travel to"
                    : "Resettle in"}{" "}
                <h2 className="text-white text-2xl lg:text-4xl font-bold tracking-tight">
                  {selectedCountry}
                </h2>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cards */}
      <section className="max-w-325 mx-auto px-5 pb-15">
        {!selectedCountry && (
          <article className="relative rounded-[22px] p-6 min-h-40 flex flex-col items-center justify-center bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px]">
            <p className="text-gray-400 dark:text-gray-500 text-[15px] font-medium">
              Select a country on the globe to view information
            </p>
          </article>
        )}
        {/* Grid is always mounted so refs are available on first click */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5.5 ${!selectedCountry ? "hidden" : ""}`}
        >
          <article className="relative rounded-[22px] p-6 min-h-80 flex flex-col bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px] transition-all duration-200 hover:shadow-[0_18px_44px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_18px_44px_rgba(0,0,0,0.45)]">
            <h3 className="flex items-center gap-2 text-[22px] mb-4 font-bold">
              <LuInfo size={24} />
              General
            </h3>
            <ul ref={generalListRef} className="list-disc pl-4.5 space-y-2.5" />
          </article>
          <article className="relative rounded-[22px] p-6 min-h-80 flex flex-col bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px] transition-all duration-200 hover:shadow-[0_18px_44px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_18px_44px_rgba(0,0,0,0.45)]">
            <h3 className="flex items-center gap-2 text-[22px] mb-4 font-bold">
              <LuListChecks size={24} />
              High level Process
            </h3>
            <ul ref={processListRef} className="list-disc pl-4.5 space-y-2.5" />
          </article>
          <article className="relative rounded-[22px] p-6 min-h-80 flex flex-col bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px] transition-all duration-200 hover:shadow-[0_18px_44px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_18px_44px_rgba(0,0,0,0.45)]">
            <h3 className="flex items-center gap-2 text-[22px] mb-4 font-bold">
              <LuLightbulb size={24} />
              Tips
            </h3>
            <ul ref={tipsListRef} className="list-disc pl-4.5 space-y-2.5" />
          </article>
          <article className="relative rounded-[22px] p-6 min-h-80 flex flex-col bg-gray-100/90 dark:bg-white/5 border border-gray-200 dark:border-white/15 shadow-[0_10px_28px_rgba(0,0,0,0.12)] dark:shadow-[0_10px_28px_rgba(0,0,0,0.28)] backdrop-blur-[10px] transition-all duration-200 hover:shadow-[0_18px_44px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_18px_44px_rgba(0,0,0,0.45)]">
            <h3 className="flex items-center gap-2 text-[22px] mb-4 font-bold">
              <LuCoins size={24} />
              Costs
            </h3>
            <ul ref={costsListRef} className="list-disc pl-4.5 space-y-2.5" />
          </article>
        </div>
      </section>

      {/* Article Section */}
      {selectedCountry &&
        DATA[selectedCountry]?.[activeTab as TabKey]?.article && (
          <ArticleSection
            tab={activeTab as TabKey}
            country={selectedCountry}
            article={DATA[selectedCountry][activeTab as TabKey].article!}
          />
        )}

      <style jsx>{`
        .globe-wrapper {
          background: #f0f4f8;
        }
        :global(.dark) .globe-wrapper {
          background: #020618;
        }
        .globe-iframe {
          width: 100%;
          height: clamp(300px, 55vw, 400px);
          border: none;
          display: block;
        }
      `}</style>
    </div>
  );
}
